// preview-build.js - says so, loudly, when this is not the real app.
//
// THE PROBLEM. A Netlify branch deploy is a byte-for-byte copy of the app on
// its own address, and it talks to the SAME Supabase project as
// seasoncurve.com. There is no second database and there is no fake data. So
// a change tried out on the preview writes real evaluations about real
// children, and the only thing on screen that says which of the two you are
// looking at is the address bar, which a phone hides as soon as you scroll.
//
// That is a mistake waiting to be made at speed: open the preview to check a
// new screen, carry on into a grading pass, and the pass lands in the live
// club from a build nobody has reviewed yet.
//
// WHAT THIS IS NOT. It is not isolation. It cannot be: previews exist so a
// change can be seen against real teams, which is where the bugs are. The bar
// is a label, not a wall, and the rule it exists to support is a human one -
// look on the preview, act on the live site.
//
// Same shape as the developer bar in clubs.js, for the same reason: a bar
// rather than a badge, because a badge is something you stop noticing. They
// can both be on screen at once (a support login on a preview), which the CSS
// stacks rather than overlaps.

const BAR_ID = 'preview-bar';

/**
 * The branch or deploy a Netlify preview address belongs to, or null when the
 * host is the real app.
 *
 * Netlify puts every non-production deploy behind a '<something>--<site>'
 * subdomain and nothing else: a branch deploy is 'preview--site.netlify.app',
 * a pull-request build 'deploy-preview-7--site.netlify.app', a permalink
 * '690f3c21--site.netlify.app'. Production is the bare 'site.netlify.app', and
 * the site's own name is allowed a single hyphen (ours is khcdragonspea-v6),
 * so the double hyphen is what separates the two and nothing shorter will do.
 *
 * A custom domain pointed at a preview would defeat this. Don't give one one.
 */
export function previewBranch(hostname) {
  const host = String(hostname || '').trim().toLowerCase();
  if (!host.endsWith('.netlify.app')) return null;
  const sub = host.slice(0, -'.netlify.app'.length);
  const cut = sub.indexOf('--');
  if (cut <= 0) return null;
  return sub.slice(0, cut);
}

/**
 * What the bar reads.
 *
 * The branch name comes LAST on purpose. The bar is one line that never wraps
 * (see .preview-bar), so on a narrow phone a long branch name is the part that
 * gets cut off, and the part that matters - that this build writes to the real
 * club's data - is the part that always survives.
 */
export function previewBarText(branch) {
  return `Preview build · live database · ${branch}`;
}

/**
 * Put the bar on the page, if this is a preview. Safe to call twice.
 *
 * Takes its document so a test can hand it a stub, the same way
 * markDeveloperSession does.
 */
export function markPreviewBuild(doc = (typeof document !== 'undefined' ? document : null)) {
  if (!doc || !doc.body) return null;
  const branch = previewBranch(doc.defaultView?.location?.hostname);
  if (!branch) return null;

  const existing = doc.getElementById(BAR_ID);
  const bar = existing || doc.createElement('div');
  bar.id = BAR_ID;
  bar.className = 'preview-bar';
  bar.textContent = previewBarText(branch);
  if (!existing) doc.body.insertBefore(bar, doc.body.firstChild);
  doc.body.classList.add('is-preview');
  return branch;
}

// Runs at import. main.js pulls this in before app.js, and index.html loads the
// bundle at the end of <body>, so the body exists by now and the bar is on
// screen before the first screen renders.
markPreviewBuild();
